import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../components/ui/toast';
import BarcodeScanner from '../components/qr/BarcodeScanner';
import VisitorInfoModal from '../components/qr/VisitorInfoModal';
import { 
  getRegistrationList, 
  updateEntryStatus, 
  deleteRegistration, 
  getRegistrationsForExport,
  getRegistrationByQR,
  markAsEntered,
  getExhibitionCounts, // 전시회별 등록자 수 함수 추가
  RegistrationListOptions, 
  RegistrationListResponse,
  RegistrationData,
  TicketType
} from '../lib/api';
import { formatPhoneNumber } from '../lib/utils';

/**
 * 등록자 목록 페이지
 * 전시회 등록자들의 상세 정보를 관리하는 페이지입니다.
 */
const RegistrationList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [listData, setListData] = useState<RegistrationListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [ageGroupFilter, setAgeGroupFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [entryStatusFilter, setEntryStatusFilter] = useState<'all' | 'entered' | 'pending'>('all');
  const [exhibitionFilter, setExhibitionFilter] = useState<'all' | 'urbanbreak' | 'toycon'>('all'); // 전시회 Tab 상태
  const [exhibitionCounts, setExhibitionCounts] = useState<{ total: number; urbanbreak: number; toycon: number; entered: number }>({ 
    total: 0, 
    urbanbreak: 0, 
    toycon: 0,
    entered: 0
  }); // 전시회별 등록자 수
  const [sortBy, setSortBy] = useState<'created_at' | 'name' | 'phone'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [isVisitorInfoModalOpen, setIsVisitorInfoModalOpen] = useState(false);
  const [selectedRegistration, setSelectedRegistration] = useState<RegistrationData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useToast();

  // URL 파라미터에서 초기 필터 설정
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam === 'pending') {
      setEntryStatusFilter('pending');
    } else if (filterParam === 'entered') {
      setEntryStatusFilter('entered');
    }
  }, [searchParams]);

  // 등록자 목록 데이터 로드
  const loadRegistrationList = useCallback(async () => {
    try {
      setIsLoading(true);
      const options: RegistrationListOptions = {
        page: currentPage,
        pageSize,
        search: searchTerm,
        genderFilter,
        ageGroupFilter,
        regionFilter,
        entryStatusFilter,
        exhibitionTypeFilter: exhibitionFilter, // 전시회 필터 추가
        sortBy,
        sortOrder
      };

      const data = await getRegistrationList(options);
      setListData(data);
    } catch (error) {
      console.error('등록자 목록 로드 실패:', error);
      if (error instanceof Error) {
        if (error.message.includes('relation "public.registrations" does not exist')) {
          showToast('⚠️ 데이터베이스 테이블이 생성되지 않았습니다. 관리자에게 문의하세요.', 'error');
        } else {
          showToast('등록자 목록을 불러오는데 실패했습니다.', 'error');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, genderFilter, ageGroupFilter, regionFilter, entryStatusFilter, exhibitionFilter, sortBy, sortOrder, showToast]);

  // 전시회별 등록자 수 로드
  const loadExhibitionCounts = useCallback(async () => {
    try {
      const counts = await getExhibitionCounts();
      setExhibitionCounts(counts);
    } catch (error) {
      console.error('전시회별 등록자 수 로드 실패:', error);
    }
  }, []);

  // 필터 변경 시 자동 검색
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setCurrentPage(1);
        loadRegistrationList();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, loadRegistrationList]);

  // 필터가 변경될 때마다 목록 다시 로드
  useEffect(() => {
    console.log('🔄 [RegistrationList] 필터 변경 감지, 목록 다시 로드');
    console.log('현재 exhibitionFilter:', exhibitionFilter);
    setCurrentPage(1); // 필터 변경 시 페이지 리셋
    loadRegistrationList();
  }, [genderFilter, ageGroupFilter, regionFilter, entryStatusFilter, exhibitionFilter, sortBy, sortOrder, loadRegistrationList]);

  // 페이지나 페이지 크기 변경 시 데이터 로드
  useEffect(() => {
    console.log('📄 [RegistrationList] 페이지 변경 감지:', currentPage, 'pageSize:', pageSize);
    loadRegistrationList();
  }, [currentPage, pageSize, loadRegistrationList]);

  // 초기 로드 시 전시회별 등록자 수 로드
  useEffect(() => {
    console.log('🚀 [RegistrationList] 컴포넌트 초기 로드');
    loadExhibitionCounts();
  }, [loadExhibitionCounts]);

  // 입장 상태 변경
  const handleEntryStatusChange = async (id: string, isEntered: boolean) => {
    try {
      await updateEntryStatus(id, isEntered);
      showToast(`입장 상태가 ${isEntered ? '입장완료' : '대기중'}으로 변경되었습니다.`, 'success');
      loadRegistrationList();
      loadExhibitionCounts(); // 전시회별 카운트 업데이트
    } catch (error) {
      showToast('입장 상태 변경에 실패했습니다.', 'error');
    }
  };

  // 등록자 정보 삭제
  const handleDeleteRegistration = async (id: string, name: string) => {
    if (!window.confirm(`정말로 "${name}" 등록자 정보를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      await deleteRegistration(id);
      showToast('등록자 정보가 삭제되었습니다.', 'success');
      loadRegistrationList();
      loadExhibitionCounts(); // 전시회별 카운트 업데이트
    } catch (error) {
      showToast('등록자 정보 삭제에 실패했습니다.', 'error');
    }
  };

  // QR코드 스캔 결과 처리
  const handleQRScanResult = async (qrCode: string) => {
    try {
      // QR코드로 등록자 정보 조회
      const registration = await getRegistrationByQR(qrCode);
      
      if (!registration) {
        showToast('해당 QR코드에 대한 등록자 정보를 찾을 수 없습니다.', 'error');
        return;
      }

      // 등록자 정보를 설정하고 팝업 열기
      setSelectedRegistration(registration);
      setIsVisitorInfoModalOpen(true);
      setIsQRScannerOpen(false);
      
    } catch (error) {
      console.error('QR코드 처리 오류:', error);
      showToast('QR코드 처리 중 오류가 발생했습니다.', 'error');
    }
  };

  // 입장 처리 핸들러
  const handleMarkAsEntered = async (registrationData: RegistrationData, ticketType: TicketType) => {
    try {
      setIsProcessing(true);
      
      // 이미 입장한 경우 체크
      if (registrationData.is_entered) {
        showToast(`${registrationData.name}님은 이미 입장 완료된 상태입니다.`, 'info');
        return;
      }

      // 입장 처리 (registration_id 사용 - 더 안전하고 직관적)
      const idToUse = registrationData.registration_id || registrationData.id || '';
      console.log('🎫 [handleMarkAsEntered] 사용할 ID:', idToUse, 'registration_id:', registrationData.registration_id, 'uuid:', registrationData.id);
      await markAsEntered(idToUse, ticketType);
      showToast(`${registrationData.name}님이 입장 처리되었습니다.`, 'success');
      
      // 목록 새로고침
      loadRegistrationList();
      loadExhibitionCounts(); // 전시회별 카운트 업데이트
      
      // 모달 닫기
      setIsVisitorInfoModalOpen(false);
      setSelectedRegistration(null);
      
    } catch (error) {
      console.error('입장 처리 오류:', error);
      showToast('입장 처리 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // 관람객 정보 모달 닫기
  const handleCloseVisitorInfoModal = () => {
    setIsVisitorInfoModalOpen(false);
    setSelectedRegistration(null);
  };

  // CSV 내보내기
  const handleExportCSV = async () => {
    try {
      const data = await getRegistrationsForExport();
      
      if (data.length === 0) {
        showToast('내보낼 데이터가 없습니다.', 'info');
        return;
      }

      // CSV 헤더
      const headers = ['이름', '전화번호', '성별', '연령대', '지역', '개인정보동의', 'SMS수신동의', '제3자제공동의', '입장상태', '등록일시'];
      
      // CSV 데이터
      const csvData = data.map(row => [
        row.name,
        row.phone,
        row.gender === 'male' ? '남성' : row.gender === 'female' ? '여성' : '기타',
        row.age_group === 'teen' ? '10대' :
        row.age_group === 'twenties' ? '20대' :
        row.age_group === 'thirties' ? '30대' :
        row.age_group === 'forties' ? '40대' :
        row.age_group === 'fifties' ? '50대' :
        row.age_group === 'sixties_plus' ? '60대 이상' : row.age_group,
        row.region,
        row.privacy_consent ? '동의' : '미동의',
        row.sms_consent ? '동의' : '미동의',
        row.third_party_consent ? '동의' : '미동의',
        row.is_entered ? '입장완료' : '대기중',
        new Date(row.created_at!).toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      ]);

      // CSV 문자열 생성
      const csvContent = [headers, ...csvData]
        .map(row => row.map(field => `"${field}"`).join(','))
        .join('\n');

      // UTF-8 BOM 추가 (한글 깨짐 방지)
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // 다운로드 실행
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `등록자목록_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showToast('CSV 파일이 다운로드되었습니다.', 'success');
    } catch (error) {
      showToast('CSV 내보내기에 실패했습니다.', 'error');
    }
  };

  // 로딩 상태 표시
  if (isLoading && !listData) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl px-4 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">등록자 목록을 불러오는 중...</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* 페이지 제목 및 네비게이션 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                등록자 목록 관리
              </h1>
              <p className="text-gray-600">
                등록된 참가자들의 상세 정보를 관리하고 입장 상태를 확인하세요.
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="outline">
                  대시보드 보기
                </Button>
              </Link>
              <Button onClick={loadRegistrationList} variant="outline">
                새로고침
              </Button>
            </div>
          </div>
        </div>

        {/* 네비게이션 탭 */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            <Link to="/admin">
              <div className="border-b-2 border-transparent py-2 px-1 hover:border-gray-300">
                <div className="flex items-center text-gray-500 hover:text-gray-700">
                  <span>📊 대시보드</span>
                </div>
              </div>
            </Link>
            <div className="border-b-2 border-blue-500 py-2 px-1">
              <div className="flex items-center">
                <span className="text-blue-600 font-medium">📋 등록자 목록</span>
              </div>
            </div>
          </nav>
        </div>

        {/* 전시회별 Tab */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  console.log('🎫 [Tab] 전체 탭 클릭');
                  setExhibitionFilter('all');
                  setCurrentPage(1); // 탭 변경 시 페이지 리셋
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  exhibitionFilter === 'all'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🎫 전체 ({exhibitionCounts.total}명)
              </button>
              <button
                onClick={() => {
                  console.log('🏢 [Tab] UrbanBreak 탭 클릭');
                  setExhibitionFilter('urbanbreak');
                  setCurrentPage(1); // 탭 변경 시 페이지 리셋
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  exhibitionFilter === 'urbanbreak'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🏢 UrbanBreak ({exhibitionCounts.urbanbreak}명)
              </button>
              <button
                onClick={() => {
                  console.log('🧸 [Tab] ToyConvention 탭 클릭');
                  setExhibitionFilter('toycon');
                  setCurrentPage(1); // 탭 변경 시 페이지 리셋
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  exhibitionFilter === 'toycon'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                🧸 ToyCon ({exhibitionCounts.toycon}명)
              </button>
            </nav>
          </div>
        </div>

        {/* 검색 및 필터 */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>검색 및 필터</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* 검색 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  검색 (이름 또는 전화번호)
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="검색어를 입력하세요"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* 성별 필터 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  성별
                </label>
                <select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">전체</option>
                  <option value="male">남성</option>
                  <option value="female">여성</option>
                </select>
              </div>

                             {/* 연령대 필터 */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   연령대
                 </label>
                 <select
                   value={ageGroupFilter}
                   onChange={(e) => setAgeGroupFilter(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="">전체</option>
                   <option value="teen">10대</option>
                   <option value="twenties">20대</option>
                   <option value="thirties">30대</option>
                   <option value="forties">40대</option>
                   <option value="fifties">50대</option>
                   <option value="sixties_plus">60대 이상</option>
                 </select>
               </div>

               {/* 지역 필터 */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   지역
                 </label>
                 <select
                   value={regionFilter}
                   onChange={(e) => setRegionFilter(e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="">전체</option>
                   <option value="seoul">서울</option>
                   <option value="busan">부산</option>
                   <option value="daegu">대구</option>
                   <option value="incheon">인천</option>
                   <option value="gwangju">광주</option>
                   <option value="daejeon">대전</option>
                   <option value="ulsan">울산</option>
                   <option value="sejong">세종</option>
                   <option value="gyeonggi">경기</option>
                   <option value="gangwon">강원</option>
                   <option value="chungbuk">충북</option>
                   <option value="chungnam">충남</option>
                   <option value="jeonbuk">전북</option>
                   <option value="jeonnam">전남</option>
                   <option value="gyeongbuk">경북</option>
                   <option value="gyeongnam">경남</option>
                   <option value="jeju">제주</option>
                   <option value="other">기타</option>
                 </select>
               </div>

               {/* 입장 상태 필터 */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   입장 상태
                 </label>
                 <select
                   value={entryStatusFilter}
                   onChange={(e) => setEntryStatusFilter(e.target.value as 'all' | 'entered' | 'pending')}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                 >
                   <option value="all">전체</option>
                   <option value="entered">입장완료</option>
                   <option value="pending">대기중</option>
                 </select>
               </div>
            </div>

            {/* 정렬 및 액션 */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-6">
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    정렬 기준
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'created_at' | 'name' | 'phone')}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="created_at">등록일시</option>
                    <option value="name">이름</option>
                    <option value="phone">전화번호</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    정렬 순서
                  </label>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="desc">내림차순</option>
                    <option value="asc">오름차순</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={() => setIsQRScannerOpen(true)} className="bg-blue-600 hover:bg-blue-700">
                  📷 QR코드 스캔
                </Button>
                <Button onClick={handleExportCSV} variant="outline">
                  CSV 내보내기
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 등록자 목록 테이블 */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>등록자 목록</CardTitle>
            <div className="text-sm text-gray-600">
              {listData ? `총 ${listData.total}명 (${listData.page}/${listData.totalPages} 페이지)` : ''}
            </div>
          </CardHeader>
          <CardContent>
            {!listData || listData.data.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📝</div>
                <p className="text-gray-500">
                  {searchTerm || genderFilter || ageGroupFilter || regionFilter || entryStatusFilter !== 'all'
                    ? '검색 조건에 맞는 등록자가 없습니다.'
                    : '등록된 사용자가 없습니다.'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-medium text-gray-900">이름</th>
                        <th className="text-left p-3 font-medium text-gray-900">전화번호</th>
                        <th className="text-left p-3 font-medium text-gray-900">성별</th>
                        <th className="text-left p-3 font-medium text-gray-900">연령대</th>
                        <th className="text-left p-3 font-medium text-gray-900">지역</th>
                        <th className="text-left p-3 font-medium text-gray-900">티켓타입</th>
                        <th className="text-left p-3 font-medium text-gray-900">입장상태</th>
                        <th className="text-left p-3 font-medium text-gray-900">등록일시</th>
                        <th className="text-left p-3 font-medium text-gray-900">액션</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listData.data.map((registration) => (
                        <tr key={registration.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-gray-900">
                            {registration.name}
                          </td>
                          <td className="p-3 text-gray-600">
                            {formatPhoneNumber(registration.phone)}
                          </td>
                          <td className="p-3 text-gray-600">
                            {registration.gender === 'male' ? '남성' : 
                             registration.gender === 'female' ? '여성' : '기타'}
                          </td>
                          <td className="p-3 text-gray-600">
                            {registration.age_group === 'teen' ? '10대' :
                             registration.age_group === 'twenties' ? '20대' :
                             registration.age_group === 'thirties' ? '30대' :
                             registration.age_group === 'forties' ? '40대' :
                             registration.age_group === 'fifties' ? '50대' :
                             registration.age_group === 'sixties_plus' ? '60대 이상' : registration.age_group}
                          </td>
                          <td className="p-3 text-gray-600">
                            {registration.region}
                          </td>
                          <td className="p-3 text-gray-600">
                            {registration.ticket_type || '-'}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              registration.is_entered 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {registration.is_entered ? '입장완료' : '대기중'}
                            </span>
                          </td>
                          <td className="p-3 text-gray-600">
                            {new Date(registration.created_at!).toLocaleDateString('ko-KR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleEntryStatusChange(registration.id!, !registration.is_entered)}
                                className={`px-2 py-1 rounded text-xs font-medium ${
                                  registration.is_entered
                                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                    : 'bg-green-100 text-green-800 hover:bg-green-200'
                                }`}
                              >
                                {registration.is_entered ? '대기로 변경' : '입장 처리'}
                              </button>
                              <button
                                onClick={() => handleDeleteRegistration(registration.id!, registration.name)}
                                className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200"
                              >
                                삭제
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 페이지네이션 */}
                {listData.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">페이지 당 표시:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={10}>10개</option>
                        <option value={20}>20개</option>
                        <option value={50}>50개</option>
                        <option value={100}>100개</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                      >
                        처음
                      </Button>
                      <Button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                      >
                        이전
                      </Button>
                      
                      <span className="px-3 py-1 text-sm">
                        {currentPage} / {listData.totalPages}
                      </span>
                      
                      <Button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === listData.totalPages}
                        variant="outline"
                        size="sm"
                      >
                        다음
                      </Button>
                      <Button
                        onClick={() => setCurrentPage(listData.totalPages)}
                        disabled={currentPage === listData.totalPages}
                        variant="outline"
                        size="sm"
                      >
                        마지막
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* 바코드 스캐너 모달 */}
        <BarcodeScanner
          isOpen={isQRScannerOpen}
          onClose={() => setIsQRScannerOpen(false)}
          onResult={handleQRScanResult}
        />

        {/* 관람객 정보 모달 */}
        <VisitorInfoModal
          isOpen={isVisitorInfoModalOpen}
          onClose={handleCloseVisitorInfoModal}
          registrationData={selectedRegistration}
          onMarkAsEntered={handleMarkAsEntered}
          isProcessing={isProcessing}
        />
      </div>
    </AdminLayout>
  );
};

export default RegistrationList; 